import React, { useState, useEffect } from 'react'
import { supportPageLinks } from '../../constants/supportPageLinks'

import * as Styled from './styled'

interface MessageProps {
  /** what type of message */
  success: boolean
  /** main point of message, respects line breaks */
  header: string
  /** explanation of message, respects line breaks */
  description: string
  /** informational text, respects line breaks */
  subtitle?: string
  /** bold message before informational text */
  subtext?: string
  /** domain details, i.e. easybib.com or citationmachine.net */
  siteName: string
}

/** template for success and failure messages */
const Message: React.FunctionComponent<MessageProps> = ({
  success,
  header,
  description,
  subtitle,
  subtext,
  siteName,
}: MessageProps) => (
  <>
    <Styled.PageWrapper>
      <Styled.Main>
        {success ? <Styled.Check /> : <Styled.XIcon />}
        <Styled.Title> {header} </Styled.Title>
        <Styled.Description>{description}</Styled.Description>
        <Styled.Subtext>
          {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}&nbsp;{subtext}
        </Styled.Subtext>
      </Styled.Main>
      <Styled.Footer>
        <Styled.FooterInfo>
          <Styled.Link href="https://www.chegg.com/termsofuse">Terms of Use</Styled.Link>
          &nbsp;| Questions?&nbsp;
          <Styled.Link href={supportPageLinks[siteName]}>
            Contact Support&nbsp;
          </Styled.Link>
        </Styled.FooterInfo>
      </Styled.Footer>
    </Styled.PageWrapper>
  </>
)

/** Performs the supplied action after the specified amount of seconds, returning a countdown value */
const useCountdown = (action: () => void, seconds: number) => {
  const [countDown, setCountDown] = useState(seconds)
  useEffect(() => {
    const countdown = setTimeout(
      () => (countDown ? setCountDown((t) => t - 1) : action()),
      1000
    )
    return () => clearTimeout(countdown)
  }, [countDown])
  return countDown
}

export interface FailureProps {
  /** website name i.e. easybib or citationmachine */
  siteName: string
  /** domain details, i.e. easybib.com or citationmachine.net */
  domainName: string
  /** name of the product i.e. EasyBib or CitationMachine */
  productName: string
  redirect: () => void
  afterSeconds: number
}

/** fullscreen failure message that counts down to redirect */
export const Failure: React.FunctionComponent<FailureProps> = ({
  redirect,
  afterSeconds,
  siteName,
  domainName,
  productName,
}: FailureProps) => {
  const secondsRemaining = useCountdown(redirect, afterSeconds)
  const success = false
  const header = 'Sorry, ad-free experience can only be\nactivated during school hours.'
  const description = `Try again from 8am - 3pm, or go to www${domainName}\nfor the regular free ${productName} experience.`
  const subtext = `You will be redirected to ad-supported ${productName} in ${secondsRemaining}.`
  return (
    <>
      <Message
        success={success}
        header={header}
        description={description}
        subtext={subtext}
        siteName={siteName}
      />
    </>
  )
}

interface SuccessProps {
  /** website name i.e. easybib or citationmachine */
  siteName: string
  /** domain details, i.e. easybib.com or citationmachine.net */
  domainName: string
}

/** fullscreen success informational message */
export const Success: React.FunctionComponent<SuccessProps> = ({
  siteName,
  domainName,
}: SuccessProps) => {
  const success = true
  const header = 'You now have access!'
  const description = `Go to www${domainName} or use the link\non your school’s website.`
  const subtitle = 'Terms:'
  const subtext =
    'Your ad-free access is only during school hours\nand you’ll need to activate access each day.'
  return (
    <>
      <Message
        success={success}
        header={header}
        description={description}
        subtitle={subtitle}
        subtext={subtext}
        siteName={siteName}
      />
    </>
  )
}
